import { useState, useEffect } from "react";
import { API, axios } from "@/api";
import { toast } from "sonner";
import { Loader2, RefreshCw, ShieldCheck, AlertTriangle, TrendingUp, DollarSign } from "lucide-react";

export default function OfferIntelligencePanel() {
  const [benchmarks, setBenchmarks] = useState([]);
  const [flagged, setFlagged] = useState([]);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(null);
  const [rebuilding, setRebuilding] = useState(false);

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const [bRes, fRes] = await Promise.all([
        axios.get(`${API}/admin/offer-intelligence/benchmarks`),
        axios.get(`${API}/admin/offer-intelligence/flagged`),
      ]);
      setBenchmarks(bRes.data.benchmarks || []);
      setFlagged(fRes.data.offers || []);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  };

  const rebuild = async () => {
    setRebuilding(true);
    try {
      const res = await axios.post(`${API}/admin/offer-intelligence/build-benchmarks`);
      toast.success(`Built ${res.data.benchmarks_built} benchmarks`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Rebuild failed");
    } finally { setRebuilding(false); }
  };

  const verify = async (bizId) => {
    setActing(bizId);
    try {
      await axios.post(`${API}/admin/offer-intelligence/verify/${bizId}`);
      toast.success("Offer verified");
      setFlagged(prev => prev.filter(f => f.id !== bizId));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Verify failed");
    } finally { setActing(null); }
  };

  if (loading) return <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>;

  return (
    <div className="space-y-4" data-testid="offer-intelligence-panel">
      {/* Summary stats */}
      <div className="grid grid-cols-3 gap-2">
        <div className="bg-sky-50 border border-sky-200 rounded-lg p-2.5 text-center">
          <p className="text-lg font-bold text-sky-700" data-testid="benchmark-count">{benchmarks.length}</p>
          <p className="text-[10px] text-sky-500">Categories</p>
        </div>
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-2.5 text-center">
          <p className="text-lg font-bold text-amber-700" data-testid="flagged-count">{flagged.length}</p>
          <p className="text-[10px] text-amber-500">Flagged</p>
        </div>
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-2.5 text-center">
          <p className="text-lg font-bold text-emerald-700">
            ${benchmarks.length > 0 ? (benchmarks.reduce((s, b) => s + b.avg_value, 0) / benchmarks.length).toFixed(0) : 0}
          </p>
          <p className="text-[10px] text-emerald-500">Avg Value</p>
        </div>
      </div>

      {/* Rebuild button */}
      <button onClick={rebuild} disabled={rebuilding}
        className="w-full flex items-center justify-center gap-1.5 py-1.5 bg-slate-100 text-slate-600 text-[11px] font-semibold rounded-lg hover:bg-slate-200 transition"
        data-testid="rebuild-benchmarks-btn">
        {rebuilding ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
        Rebuild Benchmarks
      </button>

      {/* Flagged offers */}
      {flagged.length > 0 && (
        <div data-testid="flagged-offers">
          <p className="text-[11px] font-semibold text-amber-700 mb-1.5 flex items-center gap-1">
            <AlertTriangle className="w-3.5 h-3.5" /> Flagged Offers ({flagged.length})
          </p>
          <div className="space-y-1.5 max-h-48 overflow-y-auto">
            {flagged.map((o) => (
              <div key={o.id} className="flex items-center gap-2 p-2.5 bg-amber-50 border border-amber-200 rounded-lg"
                   data-testid={`flagged-${o.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-semibold text-slate-800 truncate">{o.name}</span>
                    <span className="text-[9px] px-1.5 py-0.5 bg-red-100 text-red-700 rounded-full font-medium">
                      {o.offer_flag_reason}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 mt-0.5 text-[10px] text-slate-400">
                    <span>{o.category}</span>
                    <span>{o.city}</span>
                    <span>${o.offer_value_est}</span>
                  </div>
                </div>
                <button onClick={() => verify(o.id)} disabled={acting === o.id}
                  className="px-2 py-1 bg-emerald-50 text-emerald-600 text-[10px] font-semibold rounded hover:bg-emerald-100 transition flex-shrink-0 flex items-center gap-1"
                  data-testid={`verify-btn-${o.id}`}>
                  {acting === o.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <ShieldCheck className="w-3 h-3" />}
                  Verify
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Benchmark table */}
      {benchmarks.length > 0 && (
        <div data-testid="benchmark-table">
          <p className="text-[11px] font-semibold text-slate-600 mb-1.5 flex items-center gap-1">
            <TrendingUp className="w-3.5 h-3.5" /> Category Benchmarks
          </p>
          <div className="space-y-1 max-h-64 overflow-y-auto">
            {benchmarks.map((b) => (
              <div key={b.category} className="flex items-center gap-2 p-2 bg-white border border-slate-200 rounded-lg"
                   data-testid={`benchmark-${b.category}`}>
                <div className="flex-1 min-w-0">
                  <span className="text-xs font-semibold text-slate-800">{b.category}</span>
                  <div className="flex items-center gap-2 mt-0.5 text-[10px] text-slate-400">
                    <span>{b.count} offers</span>
                    <span>window: {b.avg_window_days}d</span>
                    {b.top_offer_types?.[0] && <span>{b.top_offer_types[0].type}</span>}
                  </div>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-xs font-bold text-slate-700 flex items-center gap-0.5">
                    <DollarSign className="w-3 h-3" />{b.avg_value?.toFixed(0)}
                  </p>
                  <p className="text-[9px] text-slate-400">avg / ${b.median_value?.toFixed(0)} med</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
